import { config } from '@iheartjane/config';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import has from 'lodash-es/has';
import { useLocation } from 'react-router';

import { api } from '@jane/shared/data-access';
import type { Kiosk } from '@jane/shared/types';

import type { KioskSessionParams } from '../../../types';
import { useSaveJwt } from './jwt';

const sessionUrl = () => `${config.apiPath}/kiosk/sessions`;

interface SessionResponseBody {
  kiosk: Kiosk;
}

export const createSession = async (kioskParams: KioskSessionParams) => {
  const response = await api.post<Response>(
    sessionUrl(),
    { kiosk: kioskParams },
    {},
    true
  );

  const body: SessionResponseBody = await response.json();
  const authHeader = response.headers.get('Authorization');

  return { authHeader, body };
};

interface UseCreateSessionOptions {
  onError?: (err: unknown) => unknown;
  onSuccess?: (response: {
    authHeader: string | null;
    body: SessionResponseBody;
  }) => unknown;
}

export const useCreateSession = (
  cartUuid?: string,
  { onError, onSuccess }: UseCreateSessionOptions = {}
) => {
  const location = useLocation();
  const mobilePath = location.pathname.includes('/mobile');
  const queryClient = useQueryClient();
  const { mutate: setJwt } = useSaveJwt();

  return useMutation({
    mutationFn: createSession,
    onError,
    onSuccess: ({ authHeader, body }) => {
      if (authHeader && !mobilePath) {
        setJwt(authHeader);
      }
      if (has(body, 'kiosk')) {
        queryClient.setQueryData(['whoami', authHeader, cartUuid], body.kiosk);
      }
      onSuccess?.({ authHeader, body });
    },
  });
};
